import { jsx } from "panel";
import uniq from "lodash/uniq";
import {
  FiltersOperator,
  GroupEventBehaviors,
  RowLabelType
} from "common/query-builder/query-entry-section/types";
import { QueryBlockEl } from "common/query-builder/query-block";
import { ButtonSize } from "common/components/button/constants";
import { generateStepRanges } from "common/widgets/step-range-selector/util";
import { SelectTheme } from "common/components/select/constants";
import {
  ChartType,
  CONVERSION_WINDOW_SIZE_LIMITS,
  CONVERSION_WINDOW_UNITS,
  MAX_HOLDING_PROPERTY_CONSTANT,
  ReportName,
  ResourceType
} from "common/report/constants";
import { ConversionQueryOptionsEl } from "common/query-builder/conversion-query-options";
import { QueryEntrySectionEl } from "common/query-builder/query-entry-section/row-types/base/query-entry-section-el";
import { BaseEntryRowEl } from "common/query-builder/query-entry-section/row-types/base/template";
import { MetricType } from "common/query-builder/query-block/constants";
import { isAnyOrderFunnel } from "common/report/funnels/util";
import { FunnelsCountType } from "common/report/funnels/models/constants";
import { FUNNELS_TIME_TO_CONVERSION } from "common/query-builder/query-block/properties-menu/constants";
import { mapBookmarkExclusionStepToEventEntry } from "common/report/funnels/models/format";
export function FunnelsMetricEntryRowEl({ section, entry, index }) {
  var _a;
  const isAnyOrder = isAnyOrderFunnel(entry.behavior.funnelOrder);
  const exclusionSteps = entry.behavior.exclusions || [];
  const currentProject = (_a = section.getContext(`currentProject`).get()) == null ? void 0 : _a.value;
  const behaviorRenderer = () => /* @__PURE__ */ jsx("div", {
    sel: ".mp-metric-entry-behavior"
  }, /* @__PURE__ */ jsx(QueryEntrySectionEl, {
    key: "funnelEntries",
    attrs: {
      ...section.helpers.getNestedSectionAttrs(),
      elref: `entrySection`,
      autofocus: true,
      "group-event-behavior": GroupEventBehaviors.Nest,
      "autoactivate-placeholders": section.getEntries().length > 1,
      "add-entry-label": `Select Step`,
      "add-entry-button-label": `Add Step`,
      "add-entry-button-tooltip-text": `Analyze users who performed each event.
[Read Intro to Funnels](https://docs.mixpanel.com/docs/analysis/reports/funnels)`,
      "min-entries": 2,
      "empty-placeholder": {
        entryIndexLabelToggle: entry.behavior.entryIndexLabelToggle,
        event: {
          custom: false,
          id: null,
          label: `Select Step`,
          renamed: null,
          value: null
        },
        filters: [],
        filtersOperator: FiltersOperator.And,
        type: ResourceType.Event
      },
      "row-draggable": true,
      "row-types": [ResourceType.Event],
      "row-label-type": RowLabelType.Numeric,
      "report-name": ReportName.Funnels,
      "show-all-events": false,
      "show-top-events": false,
      ...isAnyOrder ? { "row-index-toggleable": true, "row-index-toggle-icon": `any-order` } : null,
      entries: this.getEntries(isAnyOrder)
    },
    on: {
      update: this.handleEntryUpdate
    }
  }), /* @__PURE__ */ jsx(ConversionQueryOptionsEl, {
    attrs: {
      "event-names": uniq(entry.behavior.behaviors.map((behavior) => behavior.name)),
      "include-custom-properties": `all`,
      "project-id": section.attr(`project-id`),
      "hide-count-selector": true,
      "workspace-id": section.attr(`workspace-id`),
      selected: {
        conversionWindow: {
          unit: entry.behavior.conversionWindowUnit,
          value: entry.behavior.conversionWindowDuration
        },
        countType: entry.measurement.aggregationOperator,
        holdingPropertyConstant: entry.behavior.aggregateBy,
        funnelOrder: entry.behavior.funnelOrder
      },
      "show-sessions": true,
      "builder-params": {
        entries: this.getEntries(isAnyOrder),
        exclusionSteps: exclusionSteps.map(
          (exclusion) => mapBookmarkExclusionStepToEventEntry(this.getEntries(isAnyOrder).length, exclusion)
        ),
        dataGroupsEnabled: currentProject == null ? void 0 : currentProject.canUseCohorts(),
        dataGroupId: section.attr(`data-group-id`),
        projectHasPeople: currentProject == null ? void 0 : currentProject.hasIntegratedEngage,
        projectHasCohorts: currentProject == null ? void 0 : currentProject.canUseCohorts(),
        projectId: section.attr(`project-id`),
        workspaceId: section.attr(`workspace-id`),
        trackName: section.attr(`track-name`),
        projectAsOfDate: currentProject == null ? void 0 : currentProject.projectAsOfDate,
        entryRowLabelType: RowLabelType.None
      },
      "max-holding-property-constant": MAX_HOLDING_PROPERTY_CONSTANT,
      "conversion-window-units": CONVERSION_WINDOW_UNITS,
      "conversion-window-size-limits": CONVERSION_WINDOW_SIZE_LIMITS,
      "hide-any-order": false,
      "hide-conversion-window": false,
      "enable-sticky-header": false
    },
    on: {
      changeQueryOption: this.handleFunnelsConversionCriterialChanged
    }
  }));
  const measurementRenderer = () => {
    var _a2, _b;
    const advancedMenuSections = [
      {
        icon: `info-xs`,
        tooltip: { text: `Select the steps to measure on` },
        label: `Step Selection`,
        items: generateStepRanges({
          entry: {
            isEntireRange: entry.measurement.stepIndex === null,
            range: entry.measurement.stepIndex === null ? {} : {
              from: entry.measurement.stepIndex || 0,
              to: (entry.measurement.stepIndex || 0) + 1
            }
          },
          steps: entry.behavior.behaviors.length,
          rowLabelType: RowLabelType.Numeric,
          disableAllSteps: false,
          onlyAllSteps: section.attr(`chart-type`) === ChartType.FunnelSteps,
          isReportConsistency: true
        })
      }
    ];
    const advancedMenuLabel = (_a2 = advancedMenuSections[0].items.find((item) => item.isSelected)) == null ? void 0 : _a2.label;
    return /* @__PURE__ */ jsx("div", {
      sel: ".mp-metric-entry-measurement"
    }, /* @__PURE__ */ jsx(QueryBlockEl, {
      sel: ".count-menu",
      attrs: {
        "advanced-menu-label": advancedMenuLabel,
        "advanced-menu-sections": advancedMenuSections,
        elref: `countBlock`,
        type: `count`,
        "metric-type": MetricType.Funnel,
        "data-group-id": section.attr(`data-group-id`),
        "project-id": section.attr(`project-id`),
        "selector-type": `button`,
        "event-names": entry.behavior.behaviors.map((behavior) => behavior.name),
        selected: {
          countType: ((_b = entry.measurement.property) == null ? void 0 : _b.value) === FUNNELS_TIME_TO_CONVERSION.value ? FunnelsCountType.General : entry.measurement.aggregationOperator,
          aggregationOperator: entry.measurement.aggregationOperator,
          advancedMenuValue: {
            isEntireRange: entry.measurement.stepIndex === null,
            range: entry.measurement.stepIndex === null ? void 0 : {
              from: entry.measurement.stepIndex,
              to: entry.measurement.stepIndex + 1
            }
          },
          property: entry.measurement.property
        },
        "highlight-selections": true,
        theme: SelectTheme.QueryBuilder,
        "button-size": ButtonSize.Small,
        "track-name": `funnels`,
        "user-id": section.attr(`user-id`),
        "workspace-id": section.attr(`workspace-id`)
      },
      on: {
        select: this.handleFunnelsCountBySelected
      }
    }));
  };
  return /* @__PURE__ */ jsx(BaseEntryRowEl, {
    el: section,
    entry,
    index,
    showLabels: false,
    hideActions: true,
    rowRenderer: {
      selectorRenderer: behaviorRenderer,
      bodyRowRenderer: measurementRenderer
    },
    isPlaceholder: this.isPlaceholder()
  });
}
